import React, { useState } from 'react';
import { Feather, Clock, Check, Bookmark, RefreshCw, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';

/**
 * Gentle in-app prompt queue card — snooze, done, save, another.
 */
export function StoryPromptCard({ prompt, savedCount = 0, doneCount = 0, onAction, treeId }) {
  const [busy, setBusy] = useState(null);

  const run = async (action) => {
    if (!onAction || busy) return;
    setBusy(action);
    try {
      await onAction(action, prompt?.prompt_id);
    } finally {
      setBusy(null);
    }
  };

  if (!prompt?.text) return null;

  return (
    <div data-testid="story-prompt-card">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[hsl(38_16%_86%)] bg-white/70 px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-[0.12em] text-[hsl(32_10%_40%)]">
          <Feather className="h-3 w-3 text-[#8b7355]" aria-hidden />
          Writing prompt
        </span>
        {prompt.theme_label ? (
          <span className="text-[11px] text-muted-foreground">{prompt.theme_label}</span>
        ) : null}
        {savedCount > 0 ? (
          <span className="text-[11px] text-muted-foreground">· {savedCount} saved for later</span>
        ) : null}
      </div>

      <blockquote className="font-serif text-lg leading-relaxed text-[hsl(24_14%_18%)] sm:text-xl">
        &ldquo;{prompt.text}&rdquo;
      </blockquote>

      <p className="mt-3 text-xs leading-relaxed text-muted-foreground">
        There is no rush. Use this as a starting point when you are ready to record or write.
      </p>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-full"
          disabled={Boolean(busy)}
          onClick={() => run('snooze')}
          data-testid="prompt-snooze-btn"
        >
          {busy === 'snooze' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Clock className="mr-1 h-3.5 w-3.5" />}
          Snooze
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-full"
          disabled={Boolean(busy)}
          onClick={() => run('save')}
          data-testid="prompt-save-btn"
        >
          {busy === 'save' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Bookmark className="mr-1 h-3.5 w-3.5" />}
          Save for later
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="rounded-full"
          disabled={Boolean(busy)}
          onClick={() => run('another')}
          data-testid="prompt-another-btn"
        >
          {busy === 'another' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="mr-1 h-3.5 w-3.5" />}
          Another prompt
        </Button>
        <Button
          type="button"
          size="sm"
          className="rounded-full"
          disabled={Boolean(busy)}
          onClick={() => run('done')}
          data-testid="prompt-done-btn"
        >
          {busy === 'done' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Check className="mr-1 h-3.5 w-3.5" />}
          Mark done
        </Button>
      </div>

      {doneCount > 0 ? (
        <p className="mt-3 text-[11px] text-muted-foreground">
          You have reflected on {doneCount} {doneCount === 1 ? 'prompt' : 'prompts'} so far — thank you for showing up.
        </p>
      ) : null}
    </div>
  );
}

export default StoryPromptCard;
